import { Component, Inject, OnInit } from '@angular/core';
import { type AbstractControl, FormBuilder, FormGroup, type ValidationErrors, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ExportFormat,
  ExportPurpose,
  isValidFileName,
  type ExportCreationRequest,
} from '@cdw-ae/common-data';
import { SnackbarService } from '@cdw-ae/snackbar';
import {
  CELL_LIMIT_WARNING_THRESHOLD,
  EXPORT_FORMAT_LIMITS,
} from '../cdw-export-dialog/export-limits.constants';
import { ExportService } from '../cdw-export-dialog/export.service';
import type {
  ExportForBulkEditDialogData,
  ExportForBulkEditDialogResult,
} from './export-for-bulk-edit-dialog.interfaces';

export type ExportScope = 'selected' | 'all';

interface DropdownOption<T> {
  value: T;
  label: string;
  disabled: boolean;
  tooltip?: string;
}

@Component({
  selector: 'lib-cdw-export-for-bulk-edit-dialog',
  templateUrl: './cdw-export-for-bulk-edit-dialog.component.html',
  styleUrls: ['./cdw-export-for-bulk-edit-dialog.component.scss'],
  standalone: false,
})
export class CdwExportForBulkEditDialogComponent implements OnInit {
  exportForm!: FormGroup;
  isSubmitting = false;
  cellCountWarning: string | null = null;
  cellCountExceeded = false;

  scopeOptions: DropdownOption<ExportScope>[] = [];
  formatOptions: DropdownOption<ExportFormat>[] = [];

  constructor(
    public dialogRef: MatDialogRef<CdwExportForBulkEditDialogComponent, ExportForBulkEditDialogResult | undefined>,
    @Inject(MAT_DIALOG_DATA) public data: ExportForBulkEditDialogData,
    private readonly fb: FormBuilder,
    private readonly exportService: ExportService,
    private readonly snackbarService: SnackbarService
  ) {}

  ngOnInit(): void {
    this.scopeOptions = this.buildScopeOptions();
    const defaultScope: ExportScope = this.data.selectedCount > 0 ? 'selected' : 'all';

    this.formatOptions = this.buildFormatOptions(this.getItemCount(defaultScope));
    const defaultFormat = this.isSheetsExceeded(this.getItemCount(defaultScope))
      ? ExportFormat.CSV
      : ExportFormat.GOOGLE_SHEETS;

    const now = new Date();
    const timestamp = this.formatTimestamp(now);
    const defaultFileName = `${this.data.exportName} - ${timestamp}`;

    this.exportForm = this.fb.group({
      fileName: [defaultFileName, [Validators.required, this.validateFileName]],
      scope: [defaultScope, Validators.required],
      format: [defaultFormat, Validators.required],
    });

    this.exportForm.get('scope')?.valueChanges.subscribe((scope: ExportScope) => {
      this.onScopeChanged(scope);
    });

    this.exportForm.get('format')?.valueChanges.subscribe(() => {
      this.checkCellLimit();
    });

    this.checkCellLimit();
  }

  get currentItemCount(): number {
    const scope: ExportScope = this.exportForm?.get('scope')?.value ?? 'all';
    return this.getItemCount(scope);
  }

  get estimatedCellCount(): number {
    return (this.currentItemCount + 1) * this.data.columnCount;
  }

  onCancel(): void {
    this.dialogRef.close(undefined);
  }

  onReturnToImport(): void {
    this.dialogRef.close({
      success: false,
      requestReturnToImport: true,
    });
  }

  onExport(): void {
    if (this.exportForm.invalid || this.isSubmitting || this.cellCountExceeded) {
      return;
    }

    this.isSubmitting = true;

    const scope: ExportScope = this.exportForm.get('scope')?.value;
    const format: ExportFormat = this.exportForm.get('format')?.value;
    const fileName: string = this.exportForm.get('fileName')?.value?.trim();

    // EDUS-2486 Hit-list #7: SSRM consumers (useSelection=true) don't
    // have a client-side id list — set useSelection on the request and
    // let the backend resolve from Redis.
    const useServerSideSelection =
      scope === 'selected' && this.data.useSelection === true;
    const request: ExportCreationRequest = {
      gridState: {
        ...this.data.gridState,
        selectedRowIds:
          scope === 'selected' && !useServerSideSelection
            ? this.data.gridState.selectedRowIds
            : undefined,
      },
      purpose: ExportPurpose.IMPORT_READY,
      queryType: this.data.queryType,
      format,
      fileName,
      recipients: [this.data.userEmail],
      customerId: this.data.customerId,
      fbUserId: this.data.fbUserId,
      dbUserId: this.data.dbUserId,
      userEmail: this.data.userEmail,
      userName: this.data.userName,
      ...(useServerSideSelection
        ? { useSelection: true, viewKey: this.data.viewKey }
        : {}),
    };

    this.exportService
      .initiateExport(
        request,
        useServerSideSelection ? this.data.gridSessionId : undefined
      )
      .subscribe({
      next: (response) => {
        this.isSubmitting = false;
        this.dialogRef.close({
          success: response.success,
          exportId: response.exportId,
          format,
          exportScope: scope,
        });
      },
      error: (err: unknown) => {
        this.isSubmitting = false;
        let message = 'Failed to start export. Please try again.';

        if (err instanceof HttpErrorResponse) {
          if (err.status === 403) {
            message = 'You don\'t have permission to export. Contact your administrator.';
          } else if (err.error?.message) {
            message = err.error.message;
          }
        }

        const isDwdError = message.includes('Domain-wide delegation not configured');

        this.snackbarService.showError(message, 'Dismiss', {
          duration: isDwdError ? 0 : 10000,
          horizontalPosition: 'center',
        });
      },
    });
  }

  private getItemCount(scope: ExportScope): number {
    return scope === 'selected' ? this.data.selectedCount : this.data.totalItemCount;
  }

  private onScopeChanged(scope: ExportScope): void {
    const itemCount = this.getItemCount(scope);
    this.formatOptions = this.buildFormatOptions(itemCount);

    const currentFormat: ExportFormat = this.exportForm.get('format')?.value;
    if (this.isSheetsExceeded(itemCount) && currentFormat === ExportFormat.GOOGLE_SHEETS) {
      this.exportForm.get('format')?.setValue(ExportFormat.CSV);
    } else if (!this.isSheetsExceeded(itemCount) && currentFormat === ExportFormat.CSV) {
      this.exportForm.get('format')?.setValue(ExportFormat.GOOGLE_SHEETS);
    }

    this.checkCellLimit();
  }

  private isSheetsExceeded(itemCount: number): boolean {
    const limit = EXPORT_FORMAT_LIMITS[ExportFormat.GOOGLE_SHEETS];
    const cells = (itemCount + 1) * this.data.columnCount;
    return !!limit && cells > limit.maxCells;
  }

  private buildScopeOptions(): DropdownOption<ExportScope>[] {
    const hasSelection = this.data.selectedCount > 0;
    return [
      {
        value: 'selected',
        label: `Selected rows (${this.data.selectedCount.toLocaleString()})`,
        disabled: !hasSelection,
        tooltip: !hasSelection
          ? 'No rows are currently selected'
          : undefined,
      },
      {
        value: 'all',
        label: `All rows (${this.data.totalItemCount.toLocaleString()})`,
        disabled: false,
      },
    ];
  }

  private buildFormatOptions(itemCount: number): DropdownOption<ExportFormat>[] {
    const sheetsExceeded = this.isSheetsExceeded(itemCount);

    return [
      {
        value: ExportFormat.GOOGLE_SHEETS,
        label: 'Google Sheets',
        disabled: sheetsExceeded,
        tooltip: sheetsExceeded
          ? 'Too many cells for Google Sheets. Use CSV instead.'
          : undefined,
      },
      {
        value: ExportFormat.CSV,
        label: 'CSV',
        disabled: false,
      },
    ];
  }

  private checkCellLimit(): void {
    const format: ExportFormat = this.exportForm?.get('format')?.value;
    if (!format) {
      this.cellCountWarning = null;
      this.cellCountExceeded = false;
      return;
    }

    const limit = EXPORT_FORMAT_LIMITS[format];
    if (!limit) {
      this.cellCountWarning = null;
      this.cellCountExceeded = false;
      return;
    }

    const cells = this.estimatedCellCount;

    if (cells > limit.maxCells) {
      this.cellCountExceeded = true;
      this.cellCountWarning = `Estimated cell count (${cells.toLocaleString()}) exceeds the ${limit.displayName} limit of ${limit.maxCells.toLocaleString()} cells.`;
    } else if (cells > limit.maxCells * CELL_LIMIT_WARNING_THRESHOLD) {
      this.cellCountExceeded = false;
      this.cellCountWarning = `Estimated cell count (${cells.toLocaleString()}) is approaching the ${limit.displayName} limit of ${limit.maxCells.toLocaleString()} cells.`;
    } else {
      this.cellCountWarning = null;
      this.cellCountExceeded = false;
    }
  }

  private validateFileName(control: AbstractControl): ValidationErrors | null {
    const value = control.value as string;
    if (!value?.trim()) {
      return null;
    }
    if (!isValidFileName(value)) {
      return { invalidFileName: true };
    }
    return null;
  }

  private formatTimestamp(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}_${month}_${day}-${hours}_${minutes}_${seconds}`;
  }
}
